import React from 'react';

import { useQueryClient } from '@lumapps/base-api/react-query';
import { queryKeys as spaceNavigationQueryKeys } from '@lumapps/spaces-navigation/api';
import { sleep } from '@lumapps/utils/time/sleep';

import { spaceQueryKeys } from '../api';

export interface InvalidateSpacesQueriesOptions {
    /**
     * Delay in milliseconds before invalidating the queries.
     * Since the API can't guarantee the data will be updated in a reasonable timeframe,
     * we wait before invalidating to ensure the backend has processed the mutation.
     * @default 1000
     */
    delay?: number;
    /**
     * Whether the space navigation queries should also be invalidated.
     * Enable it only for mutations that can affect the navigation (e.g. saving space settings).
     * @default false
     */
    shouldInvalidateSpaceNavigation?: boolean;
}

/**
 * Hook to invalidate space data and space navigation queries in the React Query cache.
 *
 * Use this after mutations that update a space (e.g., saving space settings) to ensure
 * the cache is refreshed and components re-render with the latest data.
 *
 * @param options - Configuration options
 * @returns Function to invalidate space data and navigation queries
 */
export const useInvalidateSpacesQueries = (options?: InvalidateSpacesQueriesOptions) => {
    const queryClient = useQueryClient();
    const { delay = 1000, shouldInvalidateSpaceNavigation = false } = options || {};

    /**
     * Invalidates space data and navigation queries.
     * @returns Promise that resolves when invalidation is complete
     */
    return React.useCallback(async () => {
        // Wait for the backend to process the mutation
        if (delay > 0) {
            await sleep(delay);
        }

        const queryKeysToInvalidate = [
            spaceQueryKeys.data.all(),
            ...(shouldInvalidateSpaceNavigation ? [spaceNavigationQueryKeys.all()] : []),
        ];

        await Promise.all(
            queryKeysToInvalidate.map((queryKey) =>
                queryClient.invalidateQueries({
                    queryKey,
                    refetchType: 'all', // Force refetch for both active and inactive queries
                }),
            ),
        );
    }, [queryClient, delay, shouldInvalidateSpaceNavigation]);
};
